<template>
  <div>
    <div id="gantt-content" class="flex">
      <div id="gantt-task">
        <div id="gantt-task-title" class="flex items-center bg-green-600 text-white h-20" ref="task">
          <div class="border-t border-r border-b flex items-center justify-center font-bold text-xs w-48 h-full">タスク
          </div>
          <div class="border-t border-r border-b flex items-center justify-center font-bold text-xs w-date h-full">開始日
          </div>
          <div class="border-t border-r border-b flex items-center justify-center font-bold text-xs w-date h-full flex-col">
            <div>完了</div>
            <div>予定日</div>
          </div>
          <div class="border-t border-r border-b flex items-center justify-center font-bold text-xs w-date h-full">完了日
          </div>
          <div class="border-t border-r border-b flex items-center justify-center font-bold text-xs w-20 h-full">ステータス
          </div>
          <div class="border-t border-r border-b flex items-center justify-center font-bold text-xs w-24 h-full">担当
          </div>
        </div>
        <div id="gantt-task-list" class="overflow-y-hidden" :style="`height:${calendarViewHeight}px`">

          <template v-if="applyFilters">
            <div class="flex h-10 border-b">
              <div class="border-r flex items-center font-bold w-48 text-xs pl-4">
                検索
              </div>
              <div class="border-r flex items-center justify-center w-date text-xm flex-col first-filter">
                <YearMonthPicker
                    v-model="selectedStartingYearMonth"
                    title="開始日"
                />
              </div>
              <div class="border-r flex items-center justify-center w-date text-xm flex-col first-filter">
                <YearMonthPicker
                    v-model="selectedEndingYearMonth"
                    title="完了予定日"
                />
              </div>
              <div class="border-r flex items-center justify-center w-date text-xm flex-col first-filter">
                <YearMonthPicker
                    v-model="selectedCompletionYearMonth"
                    title="完了日"
                />
              </div>
              <div class="border-r flex items-center justify-center w-20 text-xm flex-col first-filter">
                <SelectListDialog
                    v-model="selectedStatusId"
                    :select-list="statusTypesList"
                    title="ステータス"
                />
              </div>
              <div class="flex items-center justify-center w-24 text-xm flex-col first-filter">
                <SelectListDialog
                    v-model="selectedUserId"
                    :select-list="usersList"
                    title="担当"
                />
              </div>
            </div>
          </template>

          <div v-for="(task,index) in displayTasks" :key="`tasks-${index}`"
               class="flex h-10 border-b"
               @dragstart="dragTask(task)"
               @dragover.prevent="dragTaskOver(task)"
               :draggable="false"
          >
            <template v-if="task.cat === 'category'">
              <div
                  class="flex items-center font-bold w-full text-sm pl-2 flex justify-between items-center bg-teal-100">
                <span>{{task.name}}</span>
                <div class="pr-4" @click="toggleCategory(task.id)">
                  <span v-if="task.collapsed">
                    <svg class="w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                  <span v-else>
                    <svg class="w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </div>
              </div>
            </template>
            <template v-else>
              <div
                  @click="editTask(task)"
                  class="border-r flex items-center font-bold w-48 text-xs pl-4"
                  :class="{'stop-click-event': !clickable}"
                  style="cursor: pointer"
              >
                {{task.F1}}
              </div>
              <div class="border-r flex items-center justify-center w-date text-xs flex-col">
                <div v-if="task.start_date">{{task.start_date.split('-')[0]}}</div>
                <div v-if="task.start_date">{{task.start_date.split('-')[1]}}-{{task.start_date.split('-')[2]}}</div>
              </div>
              <div class="border-r flex items-center justify-center w-date text-xs flex-col">
                <div v-if="task.end_date">{{task.end_date.split('-')[0]}}</div>
                <div v-if="task.end_date">{{task.end_date.split('-')[1]}}-{{task.end_date.split('-')[2]}}</div>
              </div>
              <div class="border-r flex items-center justify-center w-date text-xs flex-col">
                <div v-if="task.completion_date">{{task.completion_date.split('-')[0]}}</div>
                <div v-if="task.completion_date">{{task.completion_date.split('-')[1]}}-{{task.completion_date.split('-')[2]}}</div>
              </div>
              <div class="border-r flex items-center justify-center w-20 text-xs overflow-hidden text-ellipsis">
                {{task.greenc_mst_status_type_name}}
              </div>
              <div class="border-r flex items-center justify-center w-24 text-xs">
                {{task.user_name}}
              </div>
            </template>
          </div>
        </div>
      </div>
      <div id="gantt-calendar" class="overflow-x-scroll overflow-y-hidden border-l"
           :style="`width:${calendarViewWidth}px`" ref="calendar">
        <div id="gantt-date" class="h-20">
          <div id="gantt-year-month" class="relative h-8">
            <div v-for="(calendar,index) in calendars" :key="`dates-${index}`">
              <div
                  class="bg-indigo-700 text-white border-b border-r border-t h-8 absolute font-bold text-sm flex items-center justify-center"
                  :style="`width:${calendar.calendar*block_size}px;left:${calendar.start_block_number*block_size}px`">
                {{calendar.date}}
              </div>
            </div>
          </div>
          <div id="gantt-day" class="relative h-12">
            <div v-for="(calendar,index) in calendars" :key="`date-months-${index}`">
              <div v-for="(day,index) in calendar.days" :key="`month-days-${index}`">
                <div class="border-r border-b h-12 absolute flex items-center justify-center flex-col font-bold text-xs"
                     :class="{'bg-blue-100': day.dayOfWeek === '土', 'bg-red-100': day.dayOfWeek ==='日',
                     'bg-red-600 text-white': calendar.year=== today.year() && calendar.month === today.month()
                      && day.day === today.date()}"
                     :style="`width:${block_size}px;left:${day.block_number*block_size}px`">
                  <span>{{ day.day }}</span>
                  <span>{{ day.dayOfWeek }}</span>
                </div>
              </div>
            </div>
          </div>
          <template v-if="applyFilters">
            <div id="gantt-day-2" class="relative h-10" :style="{zIndex: !showEditDialog ? 10000 : 0}">
              <div v-for="(calendar,index) in calendars" :key="`date-months-${index}`">
                <div v-for="(day,index) in calendar.days" :key="`month-days-${index}`">
                  <div class="border-r border-b h-10 absolute flex items-center justify-center flex-col font-bold text-xs"
                       :class="{'bg-blue-100': day.dayOfWeek === '土', 'bg-red-100': day.dayOfWeek ==='日', 'bg-white': day.dayOfWeek !== '土' && day.dayOfWeek !== '日'}"
                       :style="`width:${block_size}px;left:${day.block_number*block_size}px`">
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div id="gantt-height" class="relative">
            <div v-for="(calendar,index) in calendars" :key="`bottom-month-${index}`">
              <div v-for="(day,index) in calendar.days" :key="`bottom-month-days-${index}`">
                <div class="border-r border-b absolute"
                     :class="{'bg-blue-100': day.dayOfWeek === '土', 'bg-red-100': day.dayOfWeek ==='日'}"
                     :style="`width:${block_size}px;left:${day.block_number*block_size}px;height:${calendarViewHeight}px`">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="gantt-bar-area" class="relative" :style="`width:${calendarViewWidth}px;height:${calendarViewHeight}px`">
          <div v-for="(bar,index) in taskBars" :key="bar.task.cat === 'task' ? `task-bar-${bar.task.id}` : `category-bar-${bar.task.id || index}`">
            <!-- Task bars (movable) -->
            <div v-if="bar.task.cat === 'task'"
                 :style="bar.style"
                 :class="bar.roundedClass"
                 class="absolute h-5 bg-yellow-500"
                 @mousedown="mouseDownMove(bar.task)">
              <!-- Left resize handle - only show if task starts within visible range -->
              <div v-if="bar.showLeftHandle"
                   class="absolute w-2 h-2 bg-gray-300 border border-black"
                   style="top:6px;left:-6px;cursor:col-resize"
                   @mousedown.stop="mouseDownResize(bar.task,'left')">
              </div>
              <!-- Right resize handle - only show if task ends within visible range -->
              <div v-if="bar.showRightHandle"
                   class="absolute w-2 h-2 bg-gray-300 border border-black"
                   style="top:6px;right:-6px;cursor:col-resize"
                   @mousedown.stop="mouseDownResize(bar.task,'right')">
              </div>
            </div>

            <!-- Category bars (non-movable) -->
            <div v-else-if="bar.task.cat === 'category'"
                 :style="bar.style"
                 :class="bar.roundedClass"
                 class="absolute h-5 bg-purple-500 cursor-default">
              <!-- No resize handles for categories -->
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@vuepic/vue-datepicker/dist/main.css'
import YearMonthPicker from '@/components/CDialogCustomYearMonthPIcker.vue'
import SelectListDialog from '@/components/CDialogSelectList.vue'
export default {
  name: 'GanttChart',
  components: {YearMonthPicker, SelectListDialog},
  props: {
    tasksList: {
      type: Array,
      default: () => [],
    },
    block_size: {
      default: 30
    },
    categories: {
      default: []
    },
    clickable: {
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    },
    resizable: {
      type: Boolean,
      default: false
    },
    filterStartDate: {
      type: String,
      default: null,
      validator(value) {
        if (!value) return true;
        const date = new Date(value);
        return !isNaN(date.getTime());
      }
    },
    filterEndDate: {
      type: String,
      default: null,
      validator(value) {
        if (!value) return true;
        const date = new Date(value);
        return !isNaN(date.getTime());
      }
    },
    statusTypesList: {
      type: Array,
      default: () => [],
    },
    usersList: {
      type: Array,
      default: () => [],
    },
    applyFilters: {
      type: Boolean,
      default: false
    },
    showEditDialog: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tasks: [],
      block_number: 0,
      calendars: [],
      inner_width: '',
      inner_height: '',
      task_width: '',
      task_height: '',
      position_id: 0,
      today: this.$moment(),
      dragging: false,
      pageX: '',
      element: '',
      left: '',
      width: '',
      leftResizing: false,
      rightResizing:false,
      task: '',
      selectedStartingYearMonth: '',
      selectedEndingYearMonth: '',
      selectedCompletionYearMonth: '',
      selectedStatusId: '',
      selectedUserId: '',
    }
  },
  methods: {
    validateFilterDates() {
      if (!this.filterStartDate && !this.filterEndDate) {
        return { isValid: true };
      }

      const startDate = this.filterStartDate ? this.$moment(this.filterStartDate) : null;
      const endDate = this.filterEndDate ? this.$moment(this.filterEndDate) : null;

      // Check if dates are valid
      if (startDate && !startDate.isValid()) {
        return { isValid: false, error: 'Invalid filterStartDate format' };
      }
      if (endDate && !endDate.isValid()) {
        return { isValid: false, error: 'Invalid filterEndDate format' };
      }

      // Check if start date is after end date
      if (startDate && endDate && startDate.isAfter(endDate)) {
        return { isValid: false, error: 'filterStartDate cannot be greater than filterEndDate' };
      }

      return { isValid: true };
    },
    getDays(year, month, block_number) {
      const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'];
      let days = [];
      let date = this.$moment(`${year}-${month}-01`);
      let num = date.daysInMonth();
      for (let i = 0; i < num; i++) {
        days.push({
          day: date.date(),
          dayOfWeek: dayOfWeek[date.day()],
          block_number
        })
        date.add(1, 'day');
        block_number++;
      }
      return days;
    },
    getCalendar() {
      const validation = this.validateFilterDates();
      if (!validation.isValid) {
        console.warn(`Gantt Chart: ${validation.error}`);
        this.calendars = [];
        return 0;
      }

      let block_number = 0;
      let days;
      let start_month = this.$moment(this.start_month)
      let end_month = this.$moment(this.end_month)
      let between_month = end_month.diff(start_month, 'months')
      this.calendars = []

      // If start month is after end month, don't display any calendars
      if (between_month < 0) {
        return 0;
      }

      for (let i = 0; i <= between_month; i++) {
        days = this.getDays(start_month.year(), start_month.format('MM'), block_number);
        this.calendars.push({
          date: start_month.format('YYYY年MM月'),
          year: start_month.year(),
          month: start_month.month(),
          start_block_number: block_number,
          calendar: days.length,
          days: days
        })
        start_month.add(1, 'months')
        block_number = days[days.length - 1].block_number
        block_number++;
      }
      return block_number;
    },
    getWindowSize() {
      if (this.$refs.task) {
        this.inner_width = window.innerWidth;
        this.inner_height = window.innerHeight;
        this.task_width = this.$refs.task.offsetWidth;
        this.task_height = this.$refs.task.offsetHeight;
      }
    },
    todayPosition() {
      this.$refs.calendar.scrollLeft = this.scrollDistance
    },
    windowSizeCheck() {
      let height = this.lists.length - this.position_id
      if (event.deltaY > 0 && height * 40 > this.calendarViewHeight) {
        this.position_id++
      } else if (event.deltaY < 0 && this.position_id !== 0) {
        this.position_id--
      }
    },
    mouseDownMove(task){
      // Only allow dragging for tasks, not categories
      if (this.draggable && task.cat !== 'category') {
        this.dragging = true;
        this.pageX = event.pageX;
        this.element = event.target;
        this.left = event.target.style.left;
        this.task_id = task.id
      }
    },
    mouseMove() {
      if (this.dragging) {
        let diff = this.pageX - event.pageX;
        this.element.style.left = `${parseInt(this.left.replace('px', '')) - diff}px`;
      }
    },
    stopDrag(){
      let taskToEmit = null;
      if (this.dragging) {
        let diff = this.pageX - event.pageX
        let days = Math.ceil(diff / this.block_size)
        if (days !== 0) {
          let task = this.tasks.find(task => task.id === this.task_id);
          let start_date = this.$moment(task.start_date).add(-days, 'days')
          task['start_date'] = start_date.format('YYYY-MM-DD')

          // Update completion_date if it exists, otherwise update end_date
          if (task.completion_date) {
            let completion_date = this.$moment(task.completion_date).add(-days, 'days')
            task['completion_date'] = completion_date.format('YYYY-MM-DD')
          } else {
            let end_date = this.$moment(task.end_date).add(-days, 'days')
            task['end_date'] = end_date.format('YYYY-MM-DD')
          }
          taskToEmit = task
        } else {
          this.element.style.left = `${this.left.replace('px', '')}px`;
        }
      }
      if (this.leftResizing) {
        let diff = this.pageX - event.pageX;
        let days = Math.ceil(diff / this.block_size)
        if (days !== 0) {
          let task = this.tasks.find(task => task.id === this.task_id);
          let start_date = this.$moment(task.start_date).add(-days, 'days')

          // Get the effective end date (completion_date or end_date)
          const effectiveEndDate = task.completion_date || task.end_date;
          let end_date = this.$moment(effectiveEndDate)

          if (end_date.diff(start_date, 'days') <= 0) {
            task['start_date'] = end_date.format('YYYY-MM-DD')
          } else {
            task['start_date'] = start_date.format('YYYY-MM-DD')
          }
          taskToEmit = task
        } else {
          this.element.style.width = this.width;
          this.element.style.left = `${this.left.replace('px', '')}px`;
        }
      }
      if (this.rightResizing) {
        let diff = this.pageX - event.pageX;
        let days = Math.ceil(diff / this.block_size)
        if (days === 1) {
          this.element.style.width = `${parseInt(this.width.replace('px', ''))}px`;
        } else if (days <= 2) {
          days--;
          let task = this.tasks.find(task => task.id === this.task_id);

          // Update completion_date if it exists, otherwise update end_date
          if (task.completion_date) {
            let completion_date = this.$moment(task.completion_date).add(-days, 'days')
            task['completion_date'] = completion_date.format('YYYY-MM-DD')
          } else {
            let end_date = this.$moment(task.end_date).add(-days, 'days')
            task['end_date'] = end_date.format('YYYY-MM-DD')
          }
          taskToEmit = task
        } else {
          let task = this.tasks.find(task => task.id === this.task_id);
          let start_date = this.$moment(task.start_date);

          // Update completion_date if it exists, otherwise update end_date
          if (task.completion_date) {
            let completion_date = this.$moment(task.completion_date).add(-days, 'days')
            if (completion_date.diff(start_date, 'days') < 0) {
              task['completion_date'] = start_date.format('YYYY-MM-DD')
            } else {
              task['completion_date'] = completion_date.format('YYYY-MM-DD')
            }
          } else {
            let end_date = this.$moment(task.end_date).add(-days, 'days')
            if (end_date.diff(start_date, 'days') < 0) {
              task['end_date'] = start_date.format('YYYY-MM-DD')
            } else {
              task['end_date'] = end_date.format('YYYY-MM-DD')
            }
          }
        }
      }

      if (taskToEmit !== null) {
        this.$emit('taskUpdated', taskToEmit);
      }

      this.dragging = false;
      this.leftResizing = false;
      this.rightResizing = false;
    },
    mouseDownResize(task, direction) {
      // Only allow resizing for tasks, not categories
      if (this.resizable && task.cat !== 'category') {
        direction === 'left' ? this.leftResizing = true : this.rightResizing = true;
        this.pageX = event.pageX;
        this.width = event.target.parentElement.style.width;
        this.left = event.target.parentElement.style.left;
        this.element = event.target.parentElement;
        this.task_id = task.id
      }
    },
    mouseResize() {
      if (this.leftResizing) {
        let diff = this.pageX - event.pageX
        if (parseInt(this.width.replace('px', '')) + diff > this.block_size) {
          this.element.style.width = `${parseInt(this.width.replace('px', '')) + diff}px`
          this.element.style.left = `${this.left.replace('px', '') - diff}px`;
        }
      }
      if (this.rightResizing) {
        let diff = this.pageX - event.pageX;
        if (parseInt(this.width.replace('px', '')) - diff > this.block_size) {
          this.element.style.width = `${parseInt(this.width.replace('px', '')) - diff}px`
        }
      }
    },
    dragTask(dragTask) {
      this.task = dragTask
    },
    toggleCategory(task_id) {
      let category = this.categories.find(category => category.id === task_id)
      category['collapsed'] = !category['collapsed'];
    },
    editTask(task){
      if (this.clickable) {
        this.$emit('editTask', task)
      }
    },
    dragTaskOver(overTask) {
      if (this.draggable) {
        let deleteIndex;
        let addIndex;
        if (this.task.cat !== 'category') {
          if (overTask.cat === 'category') {
            let updateTask = this.tasks.find(task => task.id === this.task.id)
            updateTask['category_id'] = overTask['id']
          } else {
            if (overTask.id !== this.task.id) {
              this.tasks.map((task, index) => { if (task.id === this.task.id) deleteIndex = index })
              this.tasks.map((task, index) => { if (task.id === overTask.id) addIndex = index })
              this.tasks.splice(deleteIndex, 1)
              this.task['category_id'] = overTask['category_id']
              this.tasks.splice(addIndex, 0, this.task)
            }
          }
        }
      }
    },
    initialize() {
      this.getCalendar()
      this.getWindowSize();
      this.$nextTick(() => {
        this.todayPosition();
      })
      window.addEventListener('resize', this.getWindowSize);
      window.addEventListener('wheel', this.windowSizeCheck);
      window.addEventListener('mousemove', this.mouseMove);
      window.addEventListener('mouseup', this.stopDrag);
      window.addEventListener('mousemove', this.mouseResize);
    },
    checkDateFilterHasValue(date) {
      return date && "year" in date && date.year;
    }
  },
  computed: {
    calendarViewWidth() {
      return this.inner_width - this.task_width;
    },
    calendarViewHeight() {
      return this.inner_height - this.task_height - 48 - 20;
    },
    scrollDistance() {
      let start_date = this.$moment(this.start_month);
      let between_days = this.today.diff(start_date, 'days')
      return (between_days + 1) * this.block_size - this.calendarViewWidth / 2;
    },
    lists() {
      let lists = [];

      const selected1Ym = this.checkDateFilterHasValue(this.selectedStartingYearMonth);
      const selected2Ym = this.checkDateFilterHasValue(this.selectedEndingYearMonth);
      const selected3Ym = this.checkDateFilterHasValue(this.selectedCompletionYearMonth);

      // Pre-format selected dates if available
      const format1 = selected1Ym ? this.$moment(this.selectedStartingYearMonth).format('YYYY-MM') : null;
      const format2 = selected2Ym ? this.$moment(this.selectedEndingYearMonth).format('YYYY-MM') : null;
      const format3 = selected3Ym ? this.$moment(this.selectedCompletionYearMonth).format('YYYY-MM') : null;

      // Filter tasks based on selected dates dynamically
      let filteredList = this.tasks.filter(task => {
        const formatStartDate = this.$moment(task.start_date).format('YYYY-MM');
        const formatEndDate = this.$moment(task.end_date).format('YYYY-MM'); // ✅ fixed (previously wrong)
        const formatCompletionDate = this.$moment(task.completion_date).format('YYYY-MM'); // ✅ fixed

        return (!format1 || formatStartDate === format1) &&
            (!format2 || formatEndDate === format2) &&
            (!format3 || formatCompletionDate === format3) &&
            (!this.selectedUserId || task.user_id === this.selectedUserId) &&
            (!this.selectedStatusId || task.greenc_mst_status_type_id === this.selectedStatusId)
      });

      // If there are no categories, return tasks directly
      if (this.categories.length === 0) {
        return filteredList.map(task => ({ cat: 'task', ...task }));
      }

      // Combine categories + tasks
      this.categories.forEach(category => {
        const findIndex = filteredList.findIndex(task => task.category_id === category.id);
        if (findIndex > -1) {
          lists.push({ cat: 'category', ...category });

          filteredList.forEach(task => {
            if (task.category_id === category.id && !category.collapsed) {
              lists.push({ cat: 'task', ...task });
            }
          });
        }
      });

      return lists;
    },
    displayTasks() {
      let display_task_number = Math.floor(this.calendarViewHeight / 40) - 1;
      return this.lists.slice(this.position_id, this.position_id + display_task_number);
    },
    taskBars() {
      if (!this.displayTasks || this.displayTasks.length === 0) {
        return [];
      }

      // If no calendars are visible (invalid date range), don't show any task bars
      if (this.calendars.length === 0) {
        return [];
      }

      const start_date = this.$moment(this.start_month).startOf('month'); // Add startOf('month')
      const end_date = this.$moment(this.end_month).endOf('month');
      const bars = [];

      this.displayTasks.forEach((task, displayIndex) => {
        if (task.start_date) {
          const task_start_date = this.$moment(task.start_date);

          // For categories, always use end_date; for tasks, prefer completion_date over end_date
          let effectiveEndDate;
          if (task.cat === 'category') {
            effectiveEndDate = task.end_date;
          } else {
            effectiveEndDate = task.completion_date || task.end_date;
          }

          const task_end_date = this.$moment(effectiveEndDate);

          // Validate dates
          if (!task_start_date.isValid() || !task_end_date.isValid()) {
            return;
          }

          // Check if task overlaps with the visible calendar range
          if (task_end_date.isBefore(start_date, 'day') || task_start_date.isAfter(end_date, 'day')) {
            return; // Task is completely outside the visible range
          }

          // Filter tasks by date range - only show bars for tasks that overlap with filter range
          if (this.filterStartDate || this.filterEndDate) {
            const filterStart = this.filterStartDate
                ? this.$moment(this.filterStartDate, "YYYY-MM").startOf("month")
                : null;
            const filterEnd = this.filterEndDate
                ? this.$moment(this.filterEndDate, "YYYY-MM").endOf("month")
                : null;


            let taskInRange = true;

            if (filterStart && filterEnd) {
              // Task overlaps if: task_start <= filter_end AND task_end >= filter_start
              taskInRange = task_start_date.isSameOrBefore(filterEnd, 'day') && task_end_date.isSameOrAfter(filterStart, 'day');
            } else if (filterStart) {
              // Task must end after or on filter start
              taskInRange = task_end_date.isSameOrAfter(filterStart, 'day');
            } else if (filterEnd) {
              // Task must start before or on filter end
              taskInRange = task_start_date.isSameOrBefore(filterEnd, 'day');
            }

            // Skip this task bar if it's outside the filter range
            if (!taskInRange) {
              return;
            }
          }

          // Calculate visible portion of the task bar
          const visible_start_date = this.$moment.max(task_start_date, start_date);
          const visible_end_date = this.$moment.min(task_end_date, end_date);

          // Calculate position and dimensions for the visible portion
          const visible_between = visible_end_date.diff(visible_start_date, 'days') + 1;

          // Only show if there are visible days
          if (visible_between <= 0) {
            return;
          }

          const visible_start = visible_start_date.diff(start_date, 'days');
          const left = visible_start * this.block_size;
          const top = 10 + (displayIndex * 40) + (this.applyFilters ? 40 : 0);

          // Determine which handles to show
          const showLeftHandle = task_start_date.isSameOrAfter(start_date, 'day');
          const showRightHandle = task_end_date.isSameOrBefore(end_date, 'day');

          // Determine rounded corners - only round the ends that are not cut off
          let roundedClass = '';
          if (showLeftHandle && showRightHandle) {
            // Both ends visible - fully rounded
            roundedClass = 'rounded-lg';
          } else if (showLeftHandle && !showRightHandle) {
            // Only left end visible - round left side only
            roundedClass = 'rounded-l-lg';
          } else if (!showLeftHandle && showRightHandle) {
            // Only right end visible - round right side only
            roundedClass = 'rounded-r-lg';
          }
          // If neither handle is visible, no rounding (straight cut on both sides)

          // Determine bar color based on task type and status
          let backgroundColor;
          if (task.cat === 'category') {
            // Purple color for categories (non-movable)
            backgroundColor = '#8B5CF6'; // purple-500
          } else {
            // Original task colors
            backgroundColor = task.greenc_mst_status_type_id === 5 ? '#156082' : '#EAB305';
          }

          bars.push({
            style: {
              top: `${top}px`,
              left: `${left}px`,
              width: `${this.block_size * visible_between}px`,
              background: backgroundColor
            },
            task,
            showLeftHandle: task.cat === 'category' ? false : showLeftHandle, // No handles for categories
            showRightHandle: task.cat === 'category' ? false : showRightHandle, // No handles for categories
            roundedClass
          });
        }
      });

      return bars;
    },
    start_month() {
      const validation = this.validateFilterDates();
      if (!validation.isValid) {
        console.warn(`Gantt Chart: ${validation.error}`);
        // Return current date as fallback
        return this.$moment().format('YYYY-MM');
      }

      // If filterStartDate is provided and valid, use it
      if (this.filterStartDate) {
        const filterStart = this.$moment(this.filterStartDate);
        if (filterStart.isValid()) {
          return filterStart.format('YYYY-MM');
        }
      }

      // If only filterEndDate is provided (and filterStartDate is empty), use filterEndDate
      if (this.filterEndDate && !this.filterStartDate) {
        const filterEnd = this.$moment(this.filterEndDate);
        if (filterEnd.isValid()) {
          return filterEnd.format('YYYY-MM');
        }
      }

      // Otherwise, use the earliest task start date (including categories)
      if (this.tasks.length === 0 && this.categories.length === 0) {
        return this.$moment().format('YYYY-MM');
      }

      const allItems = [...this.tasks, ...this.categories];
      const validStartDates = allItems
          .map(item => new Date(item.start_date))
          .filter(date => !isNaN(date.getTime()));

      if (validStartDates.length === 0) {
        return this.$moment().format('YYYY-MM');
      }

      const earliestStartDate = new Date(Math.min(...validStartDates));
      return this.$moment(earliestStartDate).format('YYYY-MM');
    },
    end_month() {
      const validation = this.validateFilterDates();
      if (!validation.isValid) {
        console.warn(`Gantt Chart: ${validation.error}`);
        // Return current date as fallback
        return this.$moment().format('YYYY-MM');
      }

      // If filterEndDate is provided and valid, use it
      if (this.filterEndDate) {
        const filterEnd = this.$moment(this.filterEndDate);
        if (filterEnd.isValid()) {
          return filterEnd.format('YYYY-MM');
        }
      }

      // If only filterStartDate is provided (and filterEndDate is empty), use filterStartDate
      if (this.filterStartDate && !this.filterEndDate) {
        const filterStart = this.$moment(this.filterStartDate);
        if (filterStart.isValid()) {
          return filterStart.format('YYYY-MM');
        }
      }

      // Otherwise, use the latest task end date (including categories)
      if (this.tasks.length === 0 && this.categories.length === 0) {
        return this.$moment().format('YYYY-MM');
      }

      // For end_month calculation, we need to consider both end_date and completion_date
      const taskEndDates = this.tasks
          .map(task => {
            const endDate = task.completion_date || task.end_date;
            return new Date(endDate);
          })
          .filter(date => !isNaN(date.getTime()));

      // For categories, always use end_date
      const categoryEndDates = this.categories
          .map(category => new Date(category.end_date))
          .filter(date => !isNaN(date.getTime()));

      const allEndDates = [...taskEndDates, ...categoryEndDates];

      if (allEndDates.length === 0) {
        return this.$moment().format('YYYY-MM');
      }

      const latestEndDate = new Date(Math.max(...allEndDates));
      return this.$moment(latestEndDate).format('YYYY-MM');
    },
  },
  beforeMount() {
    this.tasks = [...this.tasksList]
  },
  mounted() {
    this.initialize()
  },
  watch: {
    tasksList: {
      handler(newTasks) {
        this.tasks = [...newTasks]
      },
      deep: true,
    },
    start_month(newValue, oldValue) {
      if (oldValue && newValue !== oldValue) {
        this.initialize()
      }
    },
    end_month(newValue, oldValue) {
      if (oldValue && newValue !== oldValue) {
        this.initialize()
      }
    },
    filterStartDate(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initialize()
      }
    },
    filterEndDate(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initialize()
      }
    }
  }
}
</script>

<style scoped>
.stop-click-event {
  pointer-events: none;
}
.w-date {
  width: 50px;
}
.w-date-1{
  width: 60px;
}
</style>